import { Container, Graphics, Sprite, Text } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import { BINGO_COLUMN_LETTERS, type BingoCard } from './types';

/** Asset aliases registered by the host theme. */
const CARD_PAPER_ALIAS = 'bingo:card_paper';
const FREESPACE_ALIAS = 'bingo:freespace';
const DAUBER_ALIAS = 'bingo:dauber';

/**
 * Visual 5x5 bingo grid. Each cell is a tappable square showing its number
 * (or "FREE" for the centre); marked cells flip to a highlighted style.
 *
 * <p>Callers wire {@code onCellTap(col, row, number)} to handle player marks;
 * grid validates against the called-number pool externally and updates the
 * visual via {@link mark}.
 */
export class BingoCardGrid extends Container {
  private readonly cellViews: CellView[][] = [];
  private readonly cellSize: number;
  private readonly headerHeight: number;
  public onCellTap: ((col: number, row: number, number: number) => void) | null = null;

  constructor(public readonly card: BingoCard, cellSize = 64) {
    super();
    this.cellSize = cellSize;
    this.headerHeight = Math.max(28, Math.floor(cellSize * 0.5));

    // Optional commissioned card-paper backdrop — sits behind the
    // header + grid as a painted texture. Sized to cover the card with
    // a small bleed; alpha pulled back so the cell chrome still reads.
    const paperTex = AssetLoader.shared.getTexture(CARD_PAPER_ALIAS);
    if (paperTex) {
      const paper = new Sprite(paperTex);
      paper.anchor.set(0); // top-left to match grid origin
      const bleed = 16;
      paper.x = -bleed;
      paper.y = -bleed;
      paper.width = this.totalWidth() + bleed * 2;
      paper.height = this.totalHeight() + bleed * 2;
      paper.alpha = 0.95;
      this.addChild(paper);
    }

    // BINGO header row.
    const headerRow = new Container();
    this.addChild(headerRow);
    BINGO_COLUMN_LETTERS.forEach((letter, col) => {
      const bg = new Graphics();
      bg
        .roundRect(col * (cellSize + 4), 0, cellSize, this.headerHeight, 8)
        .fill({ color: 0xff6bd6, alpha: 0.95 });
      headerRow.addChild(bg);
      const label = new Text({
        text: letter,
        style: {
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: Math.floor(this.headerHeight * 0.7),
          fontWeight: '900',
          fill: 0xffffff,
          letterSpacing: 1,
        },
      });
      label.anchor.set(0.5);
      label.x = col * (cellSize + 4) + cellSize / 2;
      label.y = this.headerHeight / 2;
      headerRow.addChild(label);
    });

    const gridYOffset = this.headerHeight + 8;

    // Cells.
    for (let col = 0; col < 5; col++) {
      const colViews: CellView[] = [];
      for (let row = 0; row < 5; row++) {
        const isCentre = col === 2 && row === 2;
        const number = card.cells[col]![row]!;
        const view = new CellView(number, cellSize, isCentre);
        view.x = col * (cellSize + 4);
        view.y = gridYOffset + row * (cellSize + 4);
        view.eventMode = 'static';
        view.cursor = 'pointer';
        view.on('pointertap', () => {
          if (this.onCellTap && !isCentre) {
            this.onCellTap(col, row, number);
          }
        });
        this.addChild(view);
        colViews.push(view);

        if (isCentre) {
          view.setMarked(true);
        }
      }
      this.cellViews.push(colViews);
    }
  }

  /** Width including header and inter-cell gaps. */
  totalWidth(): number {
    return 5 * this.cellSize + 4 * 4;
  }

  totalHeight(): number {
    return this.headerHeight + 8 + 5 * this.cellSize + 4 * 4;
  }

  mark(col: number, row: number): void {
    this.cellViews[col]?.[row]?.setMarked(true);
  }

  isMarked(col: number, row: number): boolean {
    return this.cellViews[col]?.[row]?.marked ?? false;
  }

  /** Highlight the cells that contributed to a win — pulse them gold. */
  highlightWinCells(cells: readonly (readonly [number, number])[]): void {
    for (const [c, r] of cells) {
      this.cellViews[c]?.[r]?.setWinning(true);
    }
  }

  resetHighlights(): void {
    for (const col of this.cellViews) {
      for (const v of col) {
        v.setWinning(false);
      }
    }
  }
}

class CellView extends Container {
  private bg: Graphics;
  private numberText: Text;
  private freeText: Text | null = null;
  private size: number;
  public marked = false;
  private winning = false;
  /** Painterly dauber stamp shown over the cell when {@link marked}.
   * Created lazily on first mark; null when the texture isn't
   * registered (engine-only test scenes — those fall back to the bg
   * colour-flip in {@link redraw}). */
  private dauberSprite: Sprite | null = null;
  /** True when the centre FREE cell rendered the painterly freespace
   * sprite — suppresses the bg colour-flip on win/mark since the
   * sprite already carries that visual. */
  private readonly hasFreespaceSprite: boolean;

  constructor(number: number, size: number, isCentre: boolean) {
    super();
    this.size = size;

    this.bg = new Graphics();
    this.addChild(this.bg);

    this.numberText = new Text({
      text: number === 0 ? '' : String(number),
      style: {
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: Math.floor(size * 0.42),
        fontWeight: '700',
        fill: 0xffffff,
      },
    });
    this.numberText.anchor.set(0.5);
    this.numberText.x = size / 2;
    this.numberText.y = size / 2;
    this.addChild(this.numberText);

    let freespaceSpriteAdded = false;
    if (isCentre) {
      const freeTex = AssetLoader.shared.getTexture(FREESPACE_ALIAS);
      if (freeTex) {
        // Painterly FREE stamp covering the centre cell — replaces
        // the text glyph entirely.
        const sprite = new Sprite(freeTex);
        sprite.anchor.set(0.5);
        const aspect = freeTex.width / freeTex.height;
        sprite.height = size * 0.92;
        sprite.width = sprite.height * aspect;
        sprite.x = size / 2;
        sprite.y = size / 2;
        this.addChild(sprite);
        this.numberText.visible = false;
        freespaceSpriteAdded = true;
      } else {
        this.freeText = new Text({
          text: 'FREE',
          style: {
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontSize: Math.floor(size * 0.28),
            fontWeight: '900',
            fill: 0x1a0830,
            letterSpacing: 1,
          },
        });
        this.freeText.anchor.set(0.5);
        this.freeText.x = size / 2;
        this.freeText.y = size / 2;
        this.addChild(this.freeText);
        this.numberText.visible = false;
      }
    }

    this.hasFreespaceSprite = freespaceSpriteAdded;
    this.redraw();
  }

  setMarked(value: boolean): void {
    if (this.marked === value) return;
    this.marked = value;
    this.ensureDauber(value);
    this.redraw();
  }

  setWinning(value: boolean): void {
    if (this.winning === value) return;
    this.winning = value;
    this.redraw();
  }

  /** Add (or remove) the painterly dauber stamp overlay. The sprite
   * is created once and toggled via {@code visible} so repeated mark
   * toggles don't churn allocations. No-op when the texture isn't
   * registered — {@link redraw} handles the fallback colour-flip. */
  private ensureDauber(visible: boolean): void {
    if (!this.dauberSprite) {
      const tex = AssetLoader.shared.getTexture(DAUBER_ALIAS);
      if (!tex) return;
      const sprite = new Sprite(tex);
      sprite.anchor.set(0.5);
      const aspect = tex.width / tex.height;
      // Sized slightly larger than the cell so the painted edges of
      // the stamp bleed onto neighbours — mirrors a real dauber
      // splattering past the cell border.
      sprite.height = this.size * 1.05;
      sprite.width = sprite.height * aspect;
      sprite.x = this.size / 2;
      sprite.y = this.size / 2;
      this.addChild(sprite);
      this.dauberSprite = sprite;
    }
    this.dauberSprite.visible = visible;
  }

  private redraw(): void {
    this.bg.clear();
    const radius = Math.max(8, Math.floor(this.size * 0.14));
    let fill = 0x1a0830;
    let alpha = 0.7;
    let strokeColor = 0x6b3d77;
    let strokeAlpha = 0.6;

    if (this.winning) {
      fill = 0xfff59d;
      alpha = 0.95;
      strokeColor = 0xff6bd6;
      strokeAlpha = 1;
      this.numberText.style.fill = 0x1a0830;
    } else if (this.marked && !this.dauberSprite) {
      // Fallback bg colour-flip — only when no painterly dauber
      // overlay landed (engine-only test scenes).
      fill = 0xff6bd6;
      alpha = 0.85;
      strokeColor = 0xffffff;
      strokeAlpha = 0.4;
      this.numberText.style.fill = 0xffffff;
    } else {
      this.numberText.style.fill = 0xffffff;
    }

    // Suppress the bg fill entirely under the painterly freespace
    // sprite — the sprite is the cell's visual.
    if (this.hasFreespaceSprite) return;

    this.bg
      .roundRect(0, 0, this.size, this.size, radius)
      .fill({ color: fill, alpha })
      .stroke({ color: strokeColor, alpha: strokeAlpha, width: 2 });
  }
}
